// src/com/trans_plan_and_actor/TransPlanStateForCompany.js
import React, { useEffect, useRef, useState } from 'react';
//import { styled } from '@mui/material/styles';
import { Button, IconButton, CircularProgress } from '@mui/material';
import CachedIcon from '@mui/icons-material/Cached'; // refresh
import CloudDownloadIcon from '@mui/icons-material/CloudDownload'; // read from server
import NearMeIcon from '@mui/icons-material/NearMe'; // 지도로 이동 (지정 차량)
import { useLazyQuery, useReactiveVar } from '@apollo/client';
import { useSnackbar } from 'notistack';
import SearchBox from '../../common/SearchBox';
import {
    ContentConainer, ContentHeader, ControlBox, CountLabel, ToolBox
} from '../../common/TopViewControl';
import {
    AppObject, AppStyle, AppWord, CodeGroup, MapValue, TopCode
} from '../../value/AppConst';
import {
    codeTreeRepo, storeInfoListUsingRepo, transPlanAndActorListRepo,
    transPlanChangedRepo, userInfoRepo
} from '../../value/AppModel';
import { LIST_TRANS_PLAN_AND_ACTOR } from './TransPlanAndActorGql';
import { TransPlanAndActorDictionary } from './TransPlanAndActorDict';
import { useAlertSetter } from '../../api/app-hook';
import FlexyTable from '../../common/flexytable/FlexyTable';
import ValueMisc from '../../value/ValueMisc';
import DurationPicker from '../../common/DurationPicker';
import VzmSelector from '../../common/VzmSelector';
import { LIST_USING_STORE_INFO } from '../store_info/StoreInfoGql';
import AutoTextField from '../../common/AutoTextField';

// reqStateFlag 대신 tsr 발행/미발행으로만 구분해 달라해서 아래와 같이...
// 전체, Y, N. tsrIssed를 기준으로 Y/N로 표시
const reqStateOptions = [
    {codeId:AppWord.ALL, codeName:AppWord.AllKorean},
    {codeId:'Y', codeName:'발행'},
    {codeId:'N', codeName:'미발행'}
];
/**
 * 운송사용 배차현황 조회
 * VIEW
 * Using React with MUI5
 * Project: NewTOP for cloud
 * 2024-12-10 16:58:29 using DbdbTap
 * https://www.dbdbtap.com
 */
export default function TransPlanStateForCompany({
    viewServiceTags // for switching view
}) {
    const sessionInfo = useReactiveVar(userInfoRepo);
    const codeTree = useReactiveVar(codeTreeRepo);
    const transPlanChanged = useReactiveVar(transPlanChangedRepo);
    const transPlanAndActorListData = useReactiveVar(transPlanAndActorListRepo);
    const [durationChanged, setDurationChanged] = useState(false);
    const [transFlagOptions, setTransFlagOptions] = useState([{codeId:AppWord.ALL, codeName:AppWord.AllKorean}]);
    const [transFlag, setTransFlag] = useState(AppWord.ALL);
    // const [reqStateOptions, setReqStateOptions] = useState([{codeId:AppWord.ALL, codeName:AppWord.AllKorean}]);
    const [reqStateFlag, setReqStateFlag] = useState(AppWord.ALL);
    const storeInfoListUsingData = useReactiveVar(storeInfoListUsingRepo);
    const [storeOptions, setStoreOptions] = useState([{storeId:AppWord.ALL, storeName:AppWord.AllKorean}]);
    const [storeId, setStoreId] = useState(AppWord.ALL);
    const [transTypeOptions, setTransTypeOptions] = useState([{codeId:AppWord.ALL, codeName:AppWord.AllKorean}]);
    const [transTypeCd, setTransTypeCd] = useState(AppWord.ALL);
    const [transferYn, setTransferYn] = useState(AppWord.ALL);
    const [criteria, setCriteria] = useState({});
    const [searchText, setSearchText] = useState('');
    const [countToDisplay, setCountToDisplay] = useState(0);
    const addAlert = useAlertSetter();
    const { enqueueSnackbar } = useSnackbar();
    const lastReloadRef = useRef(0);
    const skipInitTransPlanChangedRef = useRef(true); // transPlanChanged 이벤트 초기회피

    // ##### Call GraphQL to get List #####
    const [getStoreInfoListUsing, responseStoreList] = useLazyQuery(LIST_USING_STORE_INFO, {
        ...AppObject.NoCachedFetch,
        onCompleted: (data, option) => {onCompleteGetStoreList(data, option)},
		onError: (error) => addAlert({title: "점포목록 조회 오류", message: [error.message]})
    });
    const [getTransPlanAndActorList, responseList] = useLazyQuery(LIST_TRANS_PLAN_AND_ACTOR, {
        ...AppObject.NoCachedFetch,
        onCompleted: (data, option) => {onCompleteGetList(data, option)},
		onError: (error) => addAlert({title: TransPlanAndActorDictionary.errorTitle.Get, message: [error.message]})
    });

    const getTransPlanAndActorListNow = (timeFrom, timeTill) => {
        const param = {variables:{timeFrom,  timeTill}};
        getTransPlanAndActorList(param);
        lastReloadRef.current = new Date().getTime()/1000;
        setDurationChanged(false);
    };

    // 초기 데이터 조회.
    useEffect(() => {
        let diff = new Date().getTime()/1000 - transPlanAndActorListData.when;

        if(diff > 300) {
            let midNight, tommorowMidNight;
            if(transPlanAndActorListData.param.timeFrom && transPlanAndActorListData.param.timeTill) {
                midNight = transPlanAndActorListData.param.timeFrom;
                tommorowMidNight = transPlanAndActorListData.param.timeTill;
            }
            else {
                midNight = ValueMisc.getYMD() + AppWord.HMS_TAIL;
                const tommorow = new Date();
                tommorow.setDate(tommorow.getDate() + 1);
                tommorowMidNight = ValueMisc.getYMD(tommorow) + AppWord.HMS_TAIL;
                const newData = {...transPlanAndActorListData};
                newData.param.timeFrom = midNight;
                newData.param.timeTill = tommorowMidNight;
                transPlanAndActorListRepo(newData);
            }
            getTransPlanAndActorListNow(midNight, tommorowMidNight);
        }
        if(!sessionInfo.user.isStoreUser()) {
            diff = new Date().getTime()/1000 - storeInfoListUsingData.when;
            if(diff > 300) getStoreInfoListUsing();
        }
    }, []);

    // 코드의 일부 추출하여 여러가지 옵션으로 만들기
    useEffect(()=>{
        // transFlag:
        const newTransFlagOptions = ValueMisc.getCodes(codeTree, CodeGroup.TRANS_FLAG);
        newTransFlagOptions.unshift({codeId:AppWord.ALL, codeName:AppWord.AllKorean});
        setTransFlagOptions(newTransFlagOptions);

        // reqState:
        /* const newReqStateOptions = ValueMisc.getCodes(codeTree, CodeGroup.REQ_STATE_FLAG);
        newReqStateOptions.unshift({codeId:AppWord.ALL, codeName:AppWord.AllKorean});
        setReqStateOptions(newReqStateOptions); */

        // transTypeCd:
        const newTransTypeOptions = ValueMisc.getCodes(codeTree, CodeGroup.TRANS_TYPE_CD);
        newTransTypeOptions.unshift({codeId:AppWord.ALL, codeName:AppWord.AllKorean});
        setTransTypeOptions(newTransTypeOptions);
    }, [codeTree]);

    // 점포목록에 {storeId:AppWord.ALL, storeName:AppWord.AllKorean} 추가
    useEffect(()=>{
        if(storeInfoListUsingData.records.length > 0) {
            const options = storeInfoListUsingData.records.map((store) => {
                return {storeId:store.storeId, storeName:store.storeName + ` [${store.storeId}]`};
            });
            options.unshift({storeId:AppWord.ALL, storeName:AppWord.AllKorean});
            setStoreOptions(options);
        }
    }, [storeInfoListUsingData]);

    // trans_plan 테이블에 추가 또는 삭제가 있을 경우 다시 읽는다.
    useEffect(() => {
        if(skipInitTransPlanChangedRef.current) {
            skipInitTransPlanChangedRef.current = false;
            return;
        }
        getTransPlanAndActorListNow(transPlanAndActorListData.param.timeFrom, transPlanAndActorListData.param.timeTill);
    }, [transPlanChanged]);


    // >>>>>>>>> callbacks <<<<<<<<<<<<<

    const onCompleteGetStoreList = (data, clientOption) => {
        if(data.storeInfoListUsing) {
            storeInfoListUsingRepo({when:new Date().getTime()/1000, records:data.storeInfoListUsing});
        }
    };

    const onCompleteGetList = (data, clientOption) => {
        if(data.transPlanAndActorList) {
            const newData = {...transPlanAndActorListData};
            newData.when = new Date().getTime()/1000;
            newData.records = data.transPlanAndActorList.map((rec) => {
                rec.tsrIssued = rec.reqStateFlag === TopCode.ReqStateFlag.TsrIssued ? "Y" : "N";
                return rec;
            });
            transPlanAndActorListRepo(newData);
        }
    };

    // >>>>>>>>> Event Handler <<<<<<<<<<<<<
    const removeFromCriteria = (key) => {
        const newCriteria = {...criteria};
        delete newCriteria[key];
        setCriteria(newCriteria);
    };

    const onChangeTransFlag = (id, value) => {
        if(value===AppWord.ALL) removeFromCriteria(id);
        else setCriteria({...criteria, [id]:value==='Y'?true:false});
        setTransFlag(value);
    };

    const onChangeReqState = (id, value) => {
        if(value===AppWord.ALL) removeFromCriteria(id);
        else setCriteria({...criteria, [id]:value});
        setReqStateFlag(value);
    };

    const onStoreSelected = (id, value) => {
        setStoreId(value);
        if(value===AppWord.ALL) removeFromCriteria(id);
        else setCriteria({...criteria, [id]:value});
    };

    const onChangeStoreInputText = (e, value, reason) => {
        if(reason === 'clear' || (reason === 'input' && value === '')) {
            removeFromCriteria('storeId');
            setStoreId(AppWord.ALL);
        }
    };

    const onChangeTransType = (id, value) => {
        setTransTypeCd(value);
        if(value===AppWord.ALL) removeFromCriteria(id);
        else setCriteria({...criteria, [id]:value});
    };

    const onChangeTransfered = (id, value) => {
        setTransferYn(value);
        if(value===AppWord.ALL) removeFromCriteria(id);
        else setCriteria({...criteria, [id]:value==='Y'?true:false});
    };

    const onClickReload = () => {
        const diff = new Date().getTime()/1000 - lastReloadRef.current;
        if(diff < 3) {
            // Too short to reload
            enqueueSnackbar(AppWord.ReloadNotIn3Sec, {variant:'info', autoHideDuration:2000});
        }
        else getTransPlanAndActorListNow(
            transPlanAndActorListData.param.timeFrom,
            transPlanAndActorListData.param.timeTill
        );
    };

    const onClickGetList = () => {
        // check from and till if they are in order
        if(transPlanAndActorListData.param.timeFrom > transPlanAndActorListData.param.timeTill) {
            addAlert({title:"조회시간 오류", message:["조회 시작시간이 종료시간보다 같거나 늦습니다."]});
            return;
        }
        getTransPlanAndActorListNow(transPlanAndActorListData.param.timeFrom, transPlanAndActorListData.param.timeTill);
    };

    const onChangeFrom = (from) => {
        const newData = {...transPlanAndActorListData};
        newData.param.timeFrom = from;
        transPlanAndActorListRepo(newData);
        setDurationChanged(true);
    };

    const onChangeTill = (till) => {
        const newData = {...transPlanAndActorListData};
        newData.param.timeTill = till;
        transPlanAndActorListRepo(newData);
        setDurationChanged(true);
    };

    const onChangeSearchText = (text) => {
        setSearchText(text);
    };

    const onDoubleClickRow = (record) => {
        if(!record.vehPlates) return;
        
        const mapUrl = ValueMisc.makeMapUrl({
                job: MapValue.MapInitEvent.traceVehicle,
                vehInfoSeqToTrace:record.vehInfoSeq,
                vehPlates:record.vehPlates,
                vehAlias:record.vehPlates
            });
        window.open(mapUrl, AppWord.MAP_WINDOW_NAME);
    };

    const onClickToolOnRow = (toolId, row) => {
        switch(toolId) {
            case AppObject.command.TO_MAP: onDoubleClickRow(row); break;
            default: return;
        }
    };

    const tools = [
        {
            id:AppObject.command.TO_MAP, icon:<NearMeIcon color={AppStyle.mui.secondary}/>,
            where:(row)=>Boolean(row.vehPlates)
        },
    ];

    const loadingAny
        = responseList.loading
        || responseStoreList.loading;

    return(
        <ContentConainer>
            {viewServiceTags}
            <ContentHeader>
                <ControlBox>
                    <DurationPicker
                        from={transPlanAndActorListData.param.timeFrom || ValueMisc.getYMD() + AppWord.HMS_TAIL}
                        onChangeFrom={onChangeFrom}
                        till={transPlanAndActorListData.param.timeTill || ValueMisc.getYMD() + ' 23:00:00'}
                        onChangeTill={onChangeTill}
                    />
                    <Button
                        variant={AppStyle.mui.contained}
                        color={AppStyle.mui.primary}
                        startIcon={<CloudDownloadIcon />}
                        onClick={onClickGetList}
                        style={{marginLeft:10}}
                        disabled={loadingAny || !durationChanged}
                    >
                        조회
                    </Button>
                </ControlBox>
                <ToolBox>
                    <CountLabel count={countToDisplay} />
                    {
                        loadingAny ?
                        <CircularProgress size={20} />
                        :
                        <IconButton
                            color={AppStyle.mui.inherit}
                            onClick={onClickReload}
                        >
                            <CachedIcon />
                        </IconButton>
                    }
                </ToolBox>
            </ContentHeader>
            <ContentHeader style={{paddingTop:0}}>
                <VzmSelector
                    dict={{
                        id: 'transFlag', valueKey: 'codeId', labelKey: 'codeName', label: '상태구분'
                    }}
                    value={transFlag}
                    onChange={onChangeTransFlag}
                    list={transFlagOptions}
                    style={{minWidth:90, marginRight:10}}
                />
                <VzmSelector
                    dict={{
                        id: 'tsrIssued', valueKey: 'codeId', labelKey: 'codeName', label: 'TSR발행여부'
                    }}
                    value={reqStateFlag}
                    list={reqStateOptions}
                    onChange={onChangeReqState}
                    style={{minWidth:120, marginRight:10}}
                />
                {
                    !sessionInfo.isStoreUser() &&
                    <AutoTextField
                        dict={{
                            id: 'storeId',
                            label: '도착지',
                            labelKey: 'storeName'
                        }}
                        value={storeId || AppWord.ALL}
                        onChange={onStoreSelected}
                        onInputChange={onChangeStoreInputText}
                        list={storeOptions}
                        style={{minWidth:250, marginRight: 10}}
                        disabled={responseStoreList.loading}
                    />
                }
                <VzmSelector
                    dict={{
                        id: 'transTypeCd', valueKey: 'codeId', label: '배차구분', labelKey: 'codeName'
                    }}
                    value={transTypeCd || AppWord.ALL}
                    list={transTypeOptions}
                    onChange={onChangeTransType}
                    style={{minWidth:90, marginRight:10}}
                />
                <VzmSelector
                    dict={{
                        id: 'transfered', valueKey: 'codeId', label: 'TSR인수인계완료', labelKey: 'codeName'
                    }}
                    value={transferYn || AppWord.ALL}
                    list={AppObject.YesNoCodeOptions}
                    onChange={onChangeTransfered}
                    style={{minWidth:110, marginRight:10}}
                />
                <ControlBox>
                    <SearchBox
                        value={searchText}
                        onChangeText={onChangeSearchText}
                        width={180}
                    />
                </ControlBox>
            </ContentHeader>
            <FlexyTable
                name={TransPlanAndActorDictionary.tablePropsForTransCom.tableId}
                tableProps={TransPlanAndActorDictionary.tablePropsForTransCom}
                records={transPlanAndActorListData.records}
                criteria={criteria}
                search={searchText}
                tools={tools}
                onClickTool={onClickToolOnRow}
                onRowDoubleClicked={onDoubleClickRow}
                onChangeCountToDisplay={setCountToDisplay}
            />
        </ContentConainer>
    );
}
